import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Breadcrumb,
  Card,
  Form,
  Input,
  Select,
  Space,
  Button,
  message,
  Spin,
  Divider,
  Switch,
  Row,
  Col,
  InputNumber,
  Tag,
  Segmented,
} from 'antd';
import { DollarOutlined, EnvironmentOutlined, CarOutlined, GlobalOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';

const { Title } = Typography;
const { Option } = Select;

const ClientDetails = () => {
  const [searchParams] = useSearchParams();
  const clientId = useMemo(() => searchParams.get('id'), [searchParams]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initial, setInitial] = useState(null);
  const [initialPricing, setInitialPricing] = useState(null);
  // Tabbed pricing controls
  const [activeTab, setActiveTab] = useState('Base Plan');
  const [basePrice, setBasePrice] = useState(499);
  // Locations
  const [locEnabled, setLocEnabled] = useState(true);
  const [locUnlimited, setLocUnlimited] = useState(false);
  const [locQty, setLocQty] = useState(0);
  const [locPrice, setLocPrice] = useState(99);
  // Fleet
  const [fleetEnabled, setFleetEnabled] = useState(true);
  const [fleetUnlimited, setFleetUnlimited] = useState(false);
  const [fleetQty, setFleetQty] = useState(0);
  const [fleetPrice, setFleetPrice] = useState(8);
  // Geo Audiences
  const [geoUnlimited, setGeoUnlimited] = useState(true);
  

  const [form] = Form.useForm();

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n || 0));
  const toNullablePositiveInt = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
  };

  const loadClient = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/clients/${clientId}`);
      if (!res.ok) throw new Error(`Load failed: ${res.status} ${res.statusText}`);
      const data = await res.json();

      let userEmail = '';
      try {
        if (data.user_id) {
          const uRes = await fetch(`/api/users/${data.user_id}`);
          if (uRes.ok) {
            const user = await uRes.json();
            userEmail = user?.email || '';
          }
        }
      } catch (_) {
        // ignore email fetch failures
      }

      const mapped = {
        name: data.name,
        email: userEmail,
        phone: '',
        status: 'active',
        agency_id: data.agency_id,
      };

      setInitial(mapped);
      form.setFieldsValue(mapped);

      // Apply pricing_config if present
      const pc = data.pricing_config || null;
      setInitialPricing(pc);
      if (pc && typeof pc === 'object') {
        if (pc.basePrice !== undefined) setBasePrice(Number(pc.basePrice) || 0);
        if (pc.locEnabled !== undefined) setLocEnabled(Boolean(pc.locEnabled));
        if (pc.locUnlimited !== undefined) setLocUnlimited(Boolean(pc.locUnlimited));
        if (pc.locQty !== undefined) setLocQty(Number(pc.locQty) || 0);
        if (pc.locPrice !== undefined) setLocPrice(Number(pc.locPrice) || 0);
        if (pc.fleetEnabled !== undefined) setFleetEnabled(Boolean(pc.fleetEnabled));
        if (pc.fleetUnlimited !== undefined) setFleetUnlimited(Boolean(pc.fleetUnlimited));
        if (pc.fleetQty !== undefined) setFleetQty(Number(pc.fleetQty) || 0);
        if (pc.fleetPrice !== undefined) setFleetPrice(Number(pc.fleetPrice) || 0);
        if (pc.geoUnlimited !== undefined) setGeoUnlimited(Boolean(pc.geoUnlimited));
      }
    } catch (e) {
      message.error(e.message || 'Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);



  const onFinish = async (values) => {
    if (!clientId) return;
    setSaving(true);
    try {
      // Compute totals similar to the calculator (normal multiplication)
      const locSubtotal = (locEnabled && !locUnlimited) ? (locQty || 0) * (locPrice || 0) : 0;
      const fleetSubtotal = (fleetEnabled && !fleetUnlimited) ? (fleetQty || 0) * (fleetPrice || 0) : 0;
      const monthlyTotal = (basePrice || 0) + locSubtotal + fleetSubtotal;

      const pricing_config = {
        basePrice,
        locEnabled,
        locUnlimited,
        locQty,
        locPrice,
        fleetEnabled,
        fleetUnlimited,
        fleetQty,
        fleetPrice,
        geoUnlimited,
      };
      const payload = {
        name: values.name,
        agency_id: toNullablePositiveInt(values.agency_id),
        pricing_config,
      };
      const token = sessionStorage.getItem('token');
      const res = await fetch(`/api/clients/${clientId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status} ${res.statusText}`);
      message.success(`Pricing saved. Monthly total: ${fmt(monthlyTotal)}`);
      setInitial(values);
      setInitialPricing(pricing_config);
    } catch (e) {
      message.error(e.message || 'Failed to update client');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = () => {
    form.setFieldsValue(initial);
    const pc = initialPricing || {};
    if (pc.basePrice !== undefined) setBasePrice(Number(pc.basePrice) || 0);
    if (pc.locEnabled !== undefined) setLocEnabled(Boolean(pc.locEnabled));
    if (pc.locUnlimited !== undefined) setLocUnlimited(Boolean(pc.locUnlimited));
    if (pc.locQty !== undefined) setLocQty(Number(pc.locQty) || 0);
    if (pc.locPrice !== undefined) setLocPrice(Number(pc.locPrice) || 0);
    if (pc.fleetEnabled !== undefined) setFleetEnabled(Boolean(pc.fleetEnabled));
    if (pc.fleetUnlimited !== undefined) setFleetUnlimited(Boolean(pc.fleetUnlimited));
    if (pc.fleetQty !== undefined) setFleetQty(Number(pc.fleetQty) || 0);
    if (pc.fleetPrice !== undefined) setFleetPrice(Number(pc.fleetPrice) || 0);
    if (pc.geoUnlimited !== undefined) setGeoUnlimited(Boolean(pc.geoUnlimited));
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb
        items={[
          { title: 'Admin' },
          { title: 'Clients' },
          { title: 'Client Account Details' },
        ]}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 12,
          marginBottom: 16,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Client Account Details
        </Title>
        <Space>
          <Button
            htmlType="submit"
            type="primary"
            form="clientForm"
            loading={saving}
          >
            Save Changes
          </Button>
        </Space>
      </div>

      <Card title={<span>Client Details</span>} style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>

          {loading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '32px 0',
              }}
            >
              <Spin />
            </div>
          ) : (
            <Form
              id="clientForm"
              layout="vertical"
              form={form}
              onFinish={onFinish}
              disabled={!clientId}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Name"
                    name="name"
                    rules={[{ required: true, message: 'Please enter name' }]}
                  >
                    <Input placeholder="Client name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      {
                        required: true,
                        type: 'email',
                        message: 'Valid email required',
                      },
                    ]}
                  >
                    <Input placeholder="Email" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Phone" name="phone">
                    <Input placeholder="Phone" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Status"
                    name="status"
                    initialValue={initial?.status || 'active'}
                  >
                    <Select>
                      <Option value="active">Active</Option>
                      <Option value="pending">Pending</Option>
                      <Option value="suspended">Suspended</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Agency ID" name="agency_id">
                    <Input type="number" min={1} placeholder="Agency ID" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />
              <Space>
                <Button onClick={handleResetAll}>Reset</Button>
                <Button type="primary" htmlType="submit" loading={saving}>
                  Save
                </Button>
              </Space>
            </Form>
          )}

          {/* Pricing Tabs */}
          <div style={{ background: '#f1f5f9', borderRadius: 10, padding: 8 }}>
            <Segmented
              value={activeTab}
              onChange={setActiveTab}
              options={[
                { label: (<span><DollarOutlined /> Base Plan</span>), value: 'Base Plan' },
                { label: (<span><EnvironmentOutlined /> Locations</span>), value: 'Locations' },
                { label: (<span><CarOutlined /> Fleet</span>), value: 'Fleet' },
                { label: (<span><GlobalOutlined /> Geo Audiences</span>), value: 'Geo Audiences' },
              ]}
              style={{ width: '100%', background: '#e2e8f0' }}
            />
          </div>

          {/* Base Plan */}
          {activeTab === 'Base Plan' && (
            <div style={{ marginTop: 12, padding: 12, border: '1px solid #e2e8f0', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ fontWeight: 600 }}>Base Plan</div>
                <div style={{ marginLeft: 'auto' }}>
                  $ <InputNumber min={0} value={basePrice} onChange={(v) => setBasePrice(Number(v) || 0)} /> per month
                </div>
              </div>
              <div style={{ color: '#64748b', marginTop: 6 }}>Core platform access, unlimited campaigns</div>
            </div>
          )}

          {/* Fleet tab */}
          {activeTab === 'Fleet' && (
            <div style={{ marginTop: 12, padding: 12, border: '1px solid #e2e8f0', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ fontWeight: 600 }}>Fleet Vehicles</div>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ color: '#64748b' }}>Unlimited</span>
                  <Switch checked={fleetUnlimited} onChange={setFleetUnlimited} />
                  <span style={{ color: '#64748b' }}>Enable</span>
                  <Switch checked={fleetEnabled} onChange={setFleetEnabled} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, opacity: fleetEnabled ? 1 : 0.5, pointerEvents: fleetEnabled ? 'auto' : 'none' }}>
                <InputNumber min={0} value={fleetQty} onChange={(v) => setFleetQty(Number(v) || 0)} disabled={fleetUnlimited} />
                <span style={{ marginLeft: 8 }}>
                  @ $ <InputNumber
                    value={fleetPrice}
                    min={0}
                    formatter={(value) => `${value}`}
                    parser={(value) => value?.replace(/\$/g, '')}
                    onChange={(v) => setFleetPrice(Number(v) || 0)}
                    step={1}
                    style={{ width: 100, margin: '0 6px' }}
                    disabled={fleetUnlimited}
                  />
                  each
                </span>
              </div>
            </div>
          )}

          {/* Geo Audiences tab */}
          {activeTab === 'Geo Audiences' && (
            <div style={{ marginTop: 12, padding: 12, border: '1px solid #e2e8f0', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ fontWeight: 600 }}>Monthly Geo Audiences</div>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ color: '#64748b' }}>Unlimited</span>
                  <Switch checked={geoUnlimited} onChange={setGeoUnlimited} />
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <Tag>No limit applied</Tag>
              </div>
            </div>
          )}


          {/* Locations tab */}
          {activeTab === 'Locations' && (
            <div style={{ marginTop: 12, padding: 12, border: '1px solid #e2e8f0', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ fontWeight: 600 }}>Business Locations</div>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ color: '#64748b' }}>Unlimited</span>
                  <Switch checked={locUnlimited} onChange={setLocUnlimited} />
                  <span style={{ color: '#64748b' }}>Enable</span>
                  <Switch checked={locEnabled} onChange={setLocEnabled} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, opacity: locEnabled ? 1 : 0.5, pointerEvents: locEnabled ? 'auto' : 'none' }}>
                <InputNumber min={0} value={locQty} onChange={(v) => setLocQty(Number(v) || 0)} disabled={locUnlimited} />
                <span style={{ marginLeft: 8 }}>
                  @ $ <InputNumber
                    value={locPrice}
                    min={0}
                    formatter={(value) => `${value}`}
                    parser={(value) => value?.replace(/\$/g, '')}
                    onChange={(v) => setLocPrice(Number(v) || 0)}
                    step={1}
                    style={{ width: 100, margin: '0 6px' }}
                    disabled={locUnlimited}
                  />
                  each
                </span>
              </div>
            </div>
          )}

          {/* Pricing Calculator */}
          <Card style={{ marginTop: 16 }} title={<span>$ Pricing Calculator</span>}>
            {(() => {
              const locBillable = locEnabled && !locUnlimited ? (locQty || 0) : 0;
              const locSubtotal = locBillable * (locPrice || 0);
              const fleetBillable = fleetEnabled && !fleetUnlimited ? (fleetQty || 0) : 0;
              const fleetSubtotal = fleetBillable * (fleetPrice || 0);
              const geoLabel = geoUnlimited ? 'Unlimited' : fmt(0);
              const total = (basePrice || 0) + locSubtotal + fleetSubtotal;
              return (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 10 }}>
                  <div>Base Plan</div>
                  <div>{fmt(basePrice)}</div>
                  <div>Number of Locations ({locUnlimited ? 'Unlimited' : (locQty || 0)})</div>
                  <div>{locUnlimited ? 'Unlimited' : fmt(locSubtotal)}</div>
                  <div>Fleet Size ({fleetUnlimited ? 'Unlimited' : (fleetQty || 0)})</div>
                  <div>{fleetUnlimited ? 'Unlimited' : fmt(fleetSubtotal)}</div>
                  <div>Geo Audiences</div>
                  <div>{geoLabel}</div>
                  <Divider style={{ gridColumn: '1 / -1' }} />
                  <div style={{ fontWeight: 700 }}>Monthly Total</div>
                  <div style={{ fontWeight: 700 }}>{fmt(total)}</div>
                </div>
              );
            })()}
          </Card>

        </Space>
      </Card>

      {/* Additional sections can be added here: billing, vehicles, workspaces, etc. */}
    </div>
  );
};

export default ClientDetails;
